import { Alert, AlertTitle, Box, Chip, Grid, ListItemButton, Stack, Tooltip, Typography } from '@mui/material';
import { Role, Visitor } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { translateRole } from 'utils/Visitor/visitor';
import { VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import EditableCard from 'v2/components/EditableCard/EditableCard';
import EditVisitorGroupsDrawer from 'v2/pages/Users/UserDetails/drawers/EditVisitorGroupsDrawer';
import { getUserGroupPath } from 'v2/paths';

interface Props {
  visitor: Visitor;
}

const GroupsSection: React.FC<Props> = ({ visitor }) => {
  const { translation } = useTranslation();
  const { openDrawer } = useDrawerContext();
  const navigate = useNavigate();
  const showRole = (role: Role) => role !== Role.None && role !== Role.ExternalGroupUser;

  const handleEditGroupsClick = () => {
    if (visitor.id) openDrawer(<EditVisitorGroupsDrawer visitorId={visitor.id} />);
  };

  return (
    <EditableCard
      title={translation.user_groups}
      editButtonId="editGroups"
      onEditClick={handleEditGroupsClick}
      spacing={2}
    >
      {visitor.visitorGroupMembership.length === 0 ? (
        <Alert severity="error" variant="standard">
          <AlertTitle>{translation.user_not_in_any_user_groups}</AlertTitle>
          <Typography variant="body2">{translation.add_user_to_user_group_hint}</Typography>
        </Alert>
      ) : (
        <Grid container spacing={0.5}>
          {visitor.visitorGroupMembership.map((membership, index) => {
            const hasChips = showRole(membership.role) || membership.visitorGroup.type === VisitorGroupType.Installers;
            return (
              <Grid key={membership.visitorGroup.id} size={12}>
                <ListItemButton
                  id={`group-membership-row-${index}`}
                  sx={{ backgroundColor: (theme) => theme.custom.background.level2, borderRadius: 1 }}
                  onClick={() => navigate(getUserGroupPath(membership.visitorGroup.id))}
                >
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                    minWidth={0}
                    rowGap={0.5}
                    columnGap={1}
                  >
                    <Tooltip title={membership.visitorGroup.name} placement="top">
                      <Box minWidth={0} overflow="hidden">
                        <Typography variant="body1" color="textPrimary" noWrap>
                          {membership.visitorGroup.name}
                        </Typography>
                      </Box>
                    </Tooltip>
                    {hasChips && (
                      <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                        {showRole(membership.role) && (
                          <Chip size="small" label={translateRole(membership.role, translation)} />
                        )}
                        {membership.visitorGroup.type === VisitorGroupType.Installers && (
                          <Chip size="small" label={translateRole('INSTALLER', translation)} />
                        )}
                      </Stack>
                    )}
                  </Stack>
                </ListItemButton>
              </Grid>
            );
          })}
        </Grid>
      )}
    </EditableCard>
  );
};

export default GroupsSection;
